import React from 'react';
import { MktPreferencePage } from '../../../../services/marketing/marketing';

// The real Wallmates mark, for a workspace without a logo set. Never a typed wordmark.
const FALLBACK_LOGO = '/logo192.png';

// #region PublicShell
/**
 * The frame for the no-login email pages: the sender's logo image, one calm card,
 * and the postal address underneath. Surface tokens follow the reader's colour scheme.
 */
export const PublicShell: React.FunctionComponent<React.PropsWithChildren<{ workspace?: MktPreferencePage['workspace'] | null }>> = ({ workspace, children }): React.ReactElement => {
    const logo = workspace?.logo_url || FALLBACK_LOGO;
    return (
        <div className="min-h-screen bg-surface-sunken text-zinc-900 dark:text-zinc-100">
            <div className="mx-auto w-full max-w-lg px-4 py-8 sm:py-14">
                <div className="mb-6 flex justify-center">
                    {/* The wordmark is dark: on a dark page it sits on a white tile so it stays legible. */}
                    <span className="inline-flex items-center justify-center rounded-xl dark:bg-white dark:px-3 dark:py-2">
                        <img src={logo} alt={workspace?.name ?? 'Wallmates'} className={workspace?.logo_url ? 'h-9 w-auto max-w-[200px] object-contain' : 'h-10 w-10'} />
                    </span>
                </div>
                <main className="rounded-2xl border border-surface-border bg-surface-raised shadow-sm">{children}</main>
                {workspace?.postal_address ? (
                    <footer className="mt-6 whitespace-pre-line text-center text-xs leading-5 text-zinc-500 dark:text-zinc-400">
                        {workspace.name}{'\n'}{workspace.postal_address}
                    </footer>
                ) : null}
            </div>
        </div>
    );
};

// #region PublicMessage
/** A short centred message: link expired, confirmed, and so on. */
export const PublicMessage: React.FunctionComponent<React.PropsWithChildren<{ title: string; body?: React.ReactNode; tone?: 'ok' | 'neutral' }>> = ({ title, body, tone = 'neutral', children }): React.ReactElement => (
    <div className="px-6 py-10 text-center">
        {tone === 'ok' ? (
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-600/10 text-emerald-700 dark:text-emerald-400" aria-hidden="true">
                <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
            </div>
        ) : null}
        <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
        {body ? <p className="mx-auto mt-2 max-w-sm text-sm text-zinc-600 dark:text-zinc-400">{body}</p> : null}
        {children}
    </div>
);
